import esTranslations from './locales/es.json';
import enTranslations from './locales/en.json';
import deTranslations from './locales/de.json';
import ptTranslations from './locales/pt.json';
import frTranslations from './locales/fr.json';
import itTranslations from './locales/it.json';
import zhTranslations from './locales/zh.json';
import jaTranslations from './locales/ja.json';

export type SupportedLocale = 'en' | 'es' | 'de' | 'pt' | 'fr' | 'it' | 'zh' | 'ja';

export const SUPPORTED_LOCALES: SupportedLocale[] = [
  'en',
  'es',
  'de',
  'pt',
  'fr',
  'it',
  'zh',
  'ja',
];

const translations: Record<SupportedLocale, any> = {
  en: enTranslations,
  es: esTranslations,
  de: deTranslations,
  pt: ptTranslations,
  fr: frTranslations,
  it: itTranslations,
  zh: zhTranslations,
  ja: jaTranslations,
};

/** Locales almacenados antes del cambio de idiomas soportados → idioma actual */
export const LEGACY_LOCALE_MAP: Record<string, SupportedLocale> = {
  'es-VE': 'es',
  ca: 'es',
  eu: 'es',
  gl: 'es',
};

/**
 * Obtiene las traducciones para un locale específico
 */
export function getTranslations(locale: SupportedLocale): any {
  return translations[locale] || translations.es;
}

/**
 * Locale BCP 47 para APIs Intl (fechas, números)
 */
export function toIntlLocale(locale: SupportedLocale): string {
  if (locale === 'zh') return 'zh-CN';
  if (locale === 'ja') return 'ja-JP';
  return locale;
}

/**
 * Detecta el locale del navegador y lo mapea a un locale soportado
 */
export function detectBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined' || !navigator.language) {
    return 'es';
  }

  const browserLocale = navigator.language.toLowerCase();

  if (browserLocale.startsWith('en')) return 'en';
  if (browserLocale.startsWith('es')) return 'es';
  if (browserLocale.startsWith('de')) return 'de';
  if (browserLocale.startsWith('pt')) return 'pt';
  if (browserLocale.startsWith('fr')) return 'fr';
  if (browserLocale.startsWith('it')) return 'it';
  if (browserLocale.startsWith('zh')) return 'zh';
  if (browserLocale.startsWith('ja')) return 'ja';

  return 'es';
}

/**
 * Función helper para obtener un valor anidado de un objeto usando notación de punto
 * Ejemplo: getNestedValue(obj, 'settings.appearance.theme.label')
 */
export function getNestedValue(obj: any, path: string): string {
  const result = path.split('.').reduce((current: any, key: string) => {
    return current && typeof current === 'object' && current[key] !== undefined ? current[key] : null;
  }, obj);

  return result !== null && typeof result === 'string' ? result : path;
}

const TRANSLATION_FALLBACK_CHAIN: SupportedLocale[] = ['en', 'es'];

/**
 * Resuelve una clave con fallback: locale actual → en → es → clave cruda
 */
export function translateWithFallback(locale: SupportedLocale, key: string): string {
  const chain: SupportedLocale[] = [
    locale,
    ...TRANSLATION_FALLBACK_CHAIN.filter((l) => l !== locale),
  ];

  for (const loc of chain) {
    const value = getNestedValue(getTranslations(loc), key);
    if (value !== key) return value;
  }

  return key;
}
